import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { ActivityLogIcon, ActivityLog, UpdateActivityLogsResponse } from "../../../models/activity-logger/activity-log.models";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { bindable } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import * as Enumerable from "linq";
import { autoinject } from "aurelia-dependency-injection";
import { computedFrom } from "aurelia-binding";
import { Messages } from "../../../configuration/event-messages.constants";
import { Reflection } from "../../../utilities/reflection";
import { ArrayUtils } from "../../../utilities/arrays";
import { SignalFunctions } from "../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { ActivityLoggerComposer, ViewModelPathWithModel } from "./activity-logger-composer";
import { BindingEngine } from "aurelia-binding";
import { I18N } from 'aurelia-i18n';
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";

@autoinject
export class ActivityLogger {

    private subscriptions: Subscription[] = [];
    private subscriptionToDocumentClickEvent: any;
    private _model: any;
    private _resolvedInstance: ViewModelPathWithModel;

    public logIcons: ActivityLogIcon[] = [];
    public logIconsDictionary: { [prop: number]: { icon: ActivityLogIcon, checked: boolean; } };
    public showLogIconsSelection: boolean = false;
    public logs: ActivityLog[] = [];
    public filteredLogs: ActivityLog[] = [];
    public _searchText: string = "";
    public sending: boolean;
    public filterOpened: boolean = false;
    public allFiltersSelected: boolean = true;
    public options = {
        weekday: "long", year: "numeric", month: "short",
        day: "numeric", hour: "2-digit", minute: "2-digit"
    };

    public newLog: ActivityLog = {
        createdBy: 0, dateCreated: (new Date()).toJSON(), userName: null,
        activityLogId: 0, entityActivityLogs: [],
        icon: null, id: 0, logIconId: 0, message: null
    };

    public set searchText(value: string) {
        this._searchText = value;
        this.filterLogs();
    }

    @computedFrom(Reflection.getPropertyPath<ActivityLogger>(z => z._searchText))
    public get searchText(): string {
        return this._searchText;
    }

    @bindable
    public entityId: number;

    @bindable
    public entityTypeId: number;

    @bindable
    public readonly: boolean = false;

    @bindable
    public sendMessage: string;

    @bindable
    public logTitle: string;

    @computedFrom(Reflection.getPropertyPath<ActivityLogger>(x => x.newLog.message))
    public get noAllSpaces(): boolean {
        if (!this.newLog || !this.newLog.message) {
            return false;
        }
        const res = this.newLog.message.replace(/ /g, "");
        return res.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityLogger>(x => x.newLog.logIconId))
    public get newLogIcon(): ActivityLogIcon {
        return this.newLog ? this.logIcons.filter(x => x.logIconId === this.newLog.logIconId)[0] : null;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityLogger>(x => x.newLog.logIconId))
    public get viewModel(): string {
        if (!this.newLogIcon || !this._resolvedInstance || this._resolvedInstance.icon != this.newLogIcon.tag) {
            this._resolvedInstance = this.newLogIcon ? this.activityLoggerResolver.get(this.newLogIcon.tag) : null;
        }
        return this._resolvedInstance ? this._resolvedInstance.viewModel : '';
    }

    @computedFrom(Reflection.getPropertyPath<ActivityLogger>(x => x.newLog.logIconId))
    public get model(): string {
        if (!this.newLogIcon || !this._resolvedInstance || this._resolvedInstance.icon != this.newLogIcon.tag) {
            this._resolvedInstance = this.newLogIcon ? this.activityLoggerResolver.get(this.newLogIcon.tag) : null;
            this._model = this._resolvedInstance ? this._resolvedInstance.modelInitiator() : null;
        }
        return this._model;
    }

    public constructor(private requestClient: RequestClientService,
        private element: Element,
        private bindingEngine: BindingEngine,
        private cacheClient: CacheClientService,
        private activityLoggerResolver: ActivityLoggerComposer,
        private signalService: SignalClientService,
        private notificationClientService: NotificationClientService,
        private eventAggrigator: EventAggregator,
        private i18N: I18N) {
        this.subscriptionToDocumentClickEvent = this.eventAggrigator.subscribe(Messages.documentClick, this.closeIconsTooltip);
        this.sendMessage = this.i18N.tr('add-text');
        this.logTitle = this.i18N.tr('history-text');
    }

    attached() {
        this.signalService.subscribe(SignalFunctions.activityLogsUpdated, this.activityLogsUpdated);
    }

    public detached(): void {
        if (this.newLog) {
            this.newLog = null;
            if (this.subscriptionToDocumentClickEvent) {
                this.subscriptionToDocumentClickEvent.dispose();
            }
        }
        this.signalService.unsubscribe(SignalFunctions.activityLogsUpdated, this.activityLogsUpdated);
    }

    public getTime(date: any): string {
        const numToTwoDigits = (num: number): string => {
            return `0${num}`.slice(-2);
        }
        const dateConverted: Date = new Date(date);
        return numToTwoDigits(dateConverted.getHours()) + ":" + numToTwoDigits(dateConverted.getMinutes());
    }

    public getDate(date: any): string {
        const options: any = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
        const dateConverted: Date = new Date(date);
        return dateConverted.toLocaleDateString("en-UK", options);
    }

    public openLogIconsSelection(event: Event): void {
        this.showLogIconsSelection = !this.showLogIconsSelection;
        event.stopPropagation();
    }

    public entityTypeIdChanged(): void {
        if (this.entityTypeId > 0 && this.entityId > 0) {
            this.reload();
        } else {
            this.logs = [];
            this.filteredLogs = [];
        }
    }

    public entityIdChanged(): void {
        if (this.entityTypeId > 0 && this.entityTypeId > 0) {
            this.reload();
        } else {
            this.logs = [];
            this.filteredLogs = [];
        }
    }

    public activityLogsUpdated = (data: UpdateActivityLogsResponse) => {
        data.created.filter(x => x.entityActivityLogs.some(y => y.entityId == this.entityId && y.entityTypeId == this.entityTypeId)).forEach(logPair => {
            this.pushLog(logPair);
        });
    }

    public reload(): void {
        if (!this.newLog) {
            this.newLog = this.getNewLog();
        }
        const body = { entityTypeId: this.entityTypeId, entityId: this.entityId };
        this.requestClient.get<ActivityLog[]>("ActivityLogs/GetActivityLogs", body)
            .then((response: ActivityLog[]) => {
                this.logs = Enumerable.from(response)
                    .orderByDescending(x => new Date(x.dateCreated))
                    .toArray();
                this.filterLogs();
                if (!this.newLog) {
                    this.newLog = this.getNewLog();
                }
                this.newLog.entityActivityLogs = [];
                this.newLog.entityActivityLogs.push({ entityId: this.entityId, entityTypeId: this.entityTypeId });
            },  () => { });
    }

    private getNewLog(): ActivityLog {
        return {
            createdBy: 0, dateCreated: (new Date()).toJSON(), userName: null,
            activityLogId: 0, entityActivityLogs: [],
            icon: null, id: 0, logIconId: 0, message: null
        };
    }

    public bind(): void {
        this.loadIcons().then(() => {
            if (this.entityTypeId > 0 && this.entityTypeId > 0) {
                this.reload();
            } else {
                this.logs = [];
                this.filteredLogs = [];
            }
        });
    }

    public selectIcon(icon: ActivityLogIcon): void {
        this.newLog.logIconId = icon.logIconId;
        this.showLogIconsSelection = false;
    }

    public getIcon(logIconId): ActivityLogIcon {
        return this.logIcons.filter(x => x.logIconId === logIconId)[0];
    }

    public addLog(newLog: ActivityLog): Promise<ActivityLog> {
        return new Promise<ActivityLog>((resolve, reject) => {
            this.requestClient.post<ActivityLog>("ActivityLogs/AddLog", {}, { activityLog: newLog, additionalData: this.model })
                .then((response: ActivityLog) => {
                    resolve(response);
                },  () => { reject(); });
        });
    }

    public messageChanged(): void {
        setTimeout(() => {
            if (this.newLog.message) {
                const indexOfColumn = this.newLog.message.indexOf(":");
                const shortcut = this.newLog.message.substr(0, indexOfColumn);
                if (shortcut.indexOf(" ") === -1) {
                    const icon = this.logIcons.filter(x => x.shortcut === shortcut)[0];
                    if (icon && this.newLog) {
                        this.newLog.logIconId = icon.logIconId;
                        this.newLog.message = this.newLog.message.substr(indexOfColumn + 1);
                    }
                }
            }
        });
    }

    public send(): Promise<any> {
        this.sending = true;
        return new Promise<void>((resolve, reject) => {
            this.addLog(this.newLog).then(log => {
                this.pushLog(log);
                this.newLog = {
                    activityLogId: 0, entityActivityLogs: [
                        { entityId: this.entityId, entityTypeId: this.entityTypeId }
                    ],
                    createdBy: 0, dateCreated: (new Date()).toJSON(), userName: null,
                    icon: null, id: 0, logIconId: this.logIcons[0].logIconId, message: null
                };
                resolve();
                this.sending = false;
            }).catch(() => {
                this.sending = false;
                reject();
            });
        });
    }

    public toggleFilter(event: Event) {
        this.filterOpened = !this.filterOpened;
        event.stopPropagation();
    }

    public selectAllFilters = () => {
        this.logIcons.forEach(logIcon => {
            this.logIconsDictionary[logIcon.logIconId].checked = this.allFiltersSelected;
        });
    }

    private pushLog(log: ActivityLog) {
        log.icon = this.logIcons.filter(x => x.logIconId == log.logIconId)[0];
        const index = ArrayUtils.firstIndex(this.logs, x => x.activityLogId > log.activityLogId);
        if (this.logs.every(x => x.activityLogId != log.activityLogId)) {
            this.logs.splice(index + 1, 0, log);
            this.filterLogs();
            this.dispatchEvent('new-log-pushed', log);
            this.notificationClientService.show(this.i18N.tr('added-new-log-successfully-text'), "", NotificationType.Success);
        }
    }

    private closeIconsTooltip = () => {
        this.showLogIconsSelection = false;
        this.filterOpened = false;
    }

    private loadIcons(): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            this.cacheClient.get<ActivityLogIcon[]>('log-icons', CacheLocation.LocalStorage, false).then(response => {
                if (!response) {
                    this.requestClient.get<ActivityLogIcon[]>("ActivityLogs/GetLogIcons", null)
                        .then((response: ActivityLogIcon[]) => {
                            this.cacheClient.put('log-icons', response);
                            this.onFetchedLogIcons(response, resolve);
                        },  () => { reject(); });
                } else {
                    this.onFetchedLogIcons(response, resolve);
                }
            });
        });
    }

    private onFetchedLogIcons(response: ActivityLogIcon[], resolve: (value?: void | PromiseLike<void>) => void) {
        this.logIcons = Enumerable.from(response).toArray();
        this.logIconsDictionary = {};
        if (this.subscriptions) {
            this.subscriptions.forEach(x => x.dispose());
            this.subscriptions = [];
        }
        this.logIcons.forEach(x => {
            this.logIconsDictionary[x.logIconId] = { icon: x, checked: true };
            //logic to maintain consistent the state of allFiltersCheckbox
            this.subscriptions.push(this.bindingEngine.propertyObserver(this.logIconsDictionary[x.logIconId], 'checked').subscribe(x => {
                if (this.allFiltersSelected && !x && this.logIcons.every(z => !this.logIconsDictionary[z.logIconId].checked)) {
                    this.allFiltersSelected = false;
                }
                else if (!this.allFiltersSelected && x && this.logIcons.every(z => this.logIconsDictionary[z.logIconId].checked)) {
                    this.allFiltersSelected = true;
                }
            }));
        });
        this.logIcons = Enumerable.from(response).where(x => x.isSelectable).toArray();
        if (this.logIcons[0] && this.newLog) {
            this.newLog.logIconId = this.logIcons[0].logIconId;
        }
        resolve();
    }

    private dispatchEvent(nameEvent: string, data?: any): void {
        if (this.element && nameEvent) {
            const customEvent: CustomEvent = new CustomEvent(nameEvent, { bubbles: true, detail: data });
            this.element.dispatchEvent(customEvent);
        }
    }

    private filterLogs() {
        this.filteredLogs = this.searchText ? this.logs.filter(x =>
            x.message.indexOf(this.searchText) > -1 ||
            this.logIconsDictionary[x.logIconId].icon.tag.indexOf(this.searchText) > -1 ||
            x.userName.indexOf(this.searchText) > -1 ||
            this.getDate(x.dateCreated).indexOf(this.searchText) > -1) : this.logs;
    }
}
